/**
 * water_bed — the ground UNDER the water looks submerged (R7 of the 2026-09-25
 * water-realism wave; lakes and rivers since the shader gaps R1, 2026-09-29).
 *
 * The terrain below sea level was lit by the full sun at any depth, and the
 * caustics landed only on the water fragment's own "floor" term — the seabed
 * itself never showed them. This layer wraps the ground material's colour
 * (the ground agent's `wetness.applyBiomeGround` calls it once, last):
 *
 *   albedo ×= exp(−σ · depth / max(L.y, 0.2))          the sun's path down
 *   albedo ×= 1 + caustic(p + L.xz/L.y·depth) · T_g     dappled light (Worley)
 *
 * — so under water the ground loses red first and wears moving caustics,
 * fading in over the first half-metre and out with depth. Procedural (no
 * sampler), skipped per fragment above the water (`If`). The sea level, sun,
 * clock and a sea-visible gate are module uniforms the water module drives
 * (`setBedWater`).
 *
 * LAKES AND RIVERS (shader gaps R1): where no sea covers the fragment, the ground reads the BODY TABLE
 * (lib/water_body `packBodyTable`: a 64² lake-id mask over the lake region, a level + turbidity per lake slot, the
 * nearest river segments) and shades against THAT body's level with THAT body's optics — lakes greener and browner,
 * rivers muddy, caustics weaker as the water turns turbid (lib/water_look OPTICS_BY_BODY: the sun path is the diffuse
 * Kd, Kirk 1984). The carrier is a uniform array, not a texture: the v2 ground's sampler budget is spent (see
 * water_body's header). THE TWO SEGMENTS LAW: this is the SUN-to-bed path only; the camera-to-bed extinction is the
 * water surface's refraction / the underwater medium. THE SEA BRANCH IS UNCHANGED (its exact look): the bodies'
 * branch is an `ElseIf` it never reaches, and `atlas:water-bodies = 'off'` (uBodiesOn 0) is today's sea-only bed.
 */
import * as THREE from 'three/webgpu';
import { Fn, If, Loop, abs, exp, float, floor, fract, int, materialColor, max, mix, mx_worley_noise_float, positionWorld, pow, smoothstep, step, uniform, uniformArray, vec3, vec4 } from 'three/tsl';
import { activePreset } from './quality_tiers.js';
import { OPTICS_BY_BODY, turbidSigma, waterLookFor } from './water_look.js';
import { extinction, POPE_FRY_ABSORPTION } from 'enginex/environment/water/waterLook.js';
import { LAKE_SLOTS, MASK_BASE, RIVER_BASE, SLOT_BASE, TABLE_VEC4, type BodyTable } from './water_body.js';

const uSeaLevel = uniform(-1e6);
const uSunDir = uniform(new THREE.Vector3(0.3, 0.9, 0.2));
const uIllum = uniform(1);
const uTime = uniform(0);
/** 1 while the Commons sea is up (0 elsewhere — personal worlds keep their ground). */
const uOn = uniform(0);

// ── The lakes and rivers (shader gaps R1) ───────────────────────────────────
const tableValues = Array.from({ length: TABLE_VEC4 }, () => new THREE.Vector4());
/** The packed body table (lib/water_body) — ONE uniform buffer binding, shared by every ground material. */
const uTable = uniformArray(tableValues, 'vec4');
/** The pack origin in RENDER space (its absolute origin + scene.position — the FP64 rebase moves it, never the table). */
const uBodyOrigin = uniform(new THREE.Vector2(0, 0));
/** 1 = the lake / river branch runs (a table is packed and `atlas:water-bodies` is not 'off'). */
const uBodiesOn = uniform(0);
/** Probe view: 1 = the bodies branch paints what it decoded (R = depth / 8, G = fract(id × φ) — each lake its own shade, B = turbidity / 2). */
const uBodiesDebug = uniform(0);
/** 1 = the SEA bed's intended mix (see the known defect in the sea branch); 0 = the 2026-09-25 output, exact. */
const uSeaBedFix = uniform(1); // the corrected sea bed by default (THE MIX LAW; `atlas:sea-bed-fix = 'off'` = the old look)
/** The two-octave Worley caustic term's mean over the bed (≈), subtracted so the bodies' dapples conserve light. */
const CAUSTIC_MEAN = 0.35;
const W = POPE_FRY_ABSORPTION;
const uWaterSigma = uniform(new THREE.Vector3(W[0], W[1], W[2]));
const LK = OPTICS_BY_BODY.lake.bedSigma, RV = OPTICS_BY_BODY.river.bedSigma;
const uLakeSigma = uniform(new THREE.Vector3(LK[0], LK[1], LK[2]));
const uRiverSigma = uniform(new THREE.Vector3(RV[0], RV[1], RV[2]));

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TSL node graphs are loosely typed (the wetness.ts alias)
type N = any;

const stats = {
  materials: 0, skipped: '', seaLevel: NaN, on: false,
  /** Shader gaps R1: the packed body table this frame. */
  bodies: { on: false, lakes: 0, rivers: 0, lakeCells: 0, riverCells: 0, origin: [0, 0] as [number, number], packs: 0 },
};
let tableOrigin: { x: number; z: number } | null = null;

/** The water module's per-frame drive. */
export function setBedWater(seaLevel: number, sunDir: THREE.Vector3, illum: number, time: number, seaVisible: boolean): void {
  (uSeaLevel as unknown as { value: number }).value = seaLevel;
  (uSunDir as unknown as { value: THREE.Vector3 }).value.copy(sunDir);
  (uIllum as unknown as { value: number }).value = illum;
  (uTime as unknown as { value: number }).value = time;
  (uOn as unknown as { value: number }).value = seaVisible ? 1 : 0;
  stats.seaLevel = seaLevel;
  stats.on = seaVisible;
}

/** Load a packed body table (lib/water_body `packBodyTable`), or null to clear it. */
export function setBedBodies(table: BodyTable | null): void {
  if (!table) { tableOrigin = null; uBodiesOn.value = 0; stats.bodies.on = false; return; }
  const d = table.data;
  for (let i = 0; i < TABLE_VEC4; i++) tableValues[i].set(d[i * 4], d[i * 4 + 1], d[i * 4 + 2], d[i * 4 + 3]);
  tableOrigin = { x: table.originX, z: table.originZ };
  stats.bodies = { on: stats.bodies.on, lakes: table.lakeCount, rivers: table.riverCount, lakeCells: table.lakeCells, riverCells: table.riverCells, origin: [table.originX, table.originZ], packs: stats.bodies.packs + 1 };
}

/** Per frame: the table's origin into render space (`scenePos` = scene.position) and the switch. */
export function setBedBodiesFrame(scenePos: { x: number; z: number }, on: boolean): void {
  const live = on && tableOrigin !== null;
  uBodiesOn.value = live ? 1 : 0;
  stats.bodies.on = live;
  if (tableOrigin) uBodyOrigin.value.set(tableOrigin.x + scenePos.x, tableOrigin.z + scenePos.z);
}

/** The sea bed's intended mix on / off (`atlas:sea-bed-fix`; off = the exact 2026-09-25 look). */
export function setSeaBedFix(on: boolean): void { uSeaBedFix.value = on ? 1 : 0; }

/** The probe's debug view of the bodies branch (depth / id / turbidity as colour). */
export function setBedBodiesDebug(on: boolean): void { uBodiesDebug.value = on ? 1 : 0; }

/** Probe counters. */
export function bedShadingStats(): { materials: number; skipped: string; seaLevel: number; on: boolean; bodies: typeof stats.bodies } {
  return { ...stats, bodies: { ...stats.bodies } };
}

/** CPU oracle: the albedo multiplier per channel at `depth` metres under the sea for a sun at `sunY`. */
export function bedAttenuation(depth: number, sunY: number): [number, number, number] {
  const st = extinction();
  const path = Math.max(0, depth) / Math.max(sunY, 0.2);
  return [Math.exp(-st[0] * path), Math.exp(-st[1] * path), Math.exp(-st[2] * path)];
}

/** CPU oracle: the same under a lake or a river of turbidity `k` (the diffuse Kd, lerped from pure water). */
export function bodyBedAttenuation(kind: 'lake' | 'river', depth: number, sunY: number, k = 1): [number, number, number] {
  const st = turbidSigma(OPTICS_BY_BODY[kind].bedSigma, kind === 'lake' ? k : 1);
  const path = Math.max(0, depth) / Math.max(sunY, 0.2);
  return [Math.exp(-st[0] * path), Math.exp(-st[1] * path), Math.exp(-st[2] * path)];
}

/**
 * Wrap a ground material's colour with the submerged-bed shading (once per
 * material; a no-op when the tier's `waterBedCaustics` is off).
 */
export function underwaterBedShading(material: THREE.Material & { colorNode?: unknown; userData: Record<string, unknown> }): boolean {
  if (material.userData.underwaterBed) return false;
  if (!waterLookFor(activePreset()).bedCaustics) { stats.skipped = 'tier'; return false; }
  const prev = material.colorNode;
  const st = extinction();
  const sigma = vec3(st[0], st[1], st[2]);
  const lakeCaustic = OPTICS_BY_BODY.lake.caustic, riverCaustic = OPTICS_BY_BODY.river.caustic;
  material.colorNode = Fn(() => {
    const base = vec4((prev ?? materialColor) as never);
    const col = vec3(base.rgb).toVar();
    // One body's bed at `d` under ITS water with ITS σ (the sea's recipe, a caustic weight for the turbidity).
    const shadeBody = (d: N, sig: N, cw: number): void => {
      const L = uSunDir;
      const ly = max(L.y, 0.2);
      const trans = exp(sig.negate().mul(d.div(ly)));
      const px = positionWorld.x.add(L.x.div(ly).mul(d));
      const pz = positionWorld.z.add(L.z.div(ly).mul(d));
      const cUv = vec3(px.mul(0.55), pz.mul(0.55), uTime.mul(0.35));
      // PERF (shader sweep C, 2026-10-06): the dapple is × sunUp = smoothstep(0, 0.15, L.y), EXACTLY 0 with the sun down —
      // but the two 27-cell 3D Worleys ran under every lake and river all night. Gated on the UNIFORM sun (1 + 0 = 1).
      const caustic = float(0).toVar();
      If(L.y.greaterThan(0), () => {
        const w1 = mx_worley_noise_float(cUv);
        const w2 = mx_worley_noise_float(cUv.mul(1.9).add(vec3(3.1, 5.7, 0)));
        // Mean-centred (the Worley term averages ≈ CAUSTIC_MEAN): dapples REDISTRIBUTE the sun on the bed, never add it.
        caustic.assign(pow(float(1).sub(w1.mul(w2)), 6).sub(CAUSTIC_MEAN));
      });
      const inWater = smoothstep(0.02, 0.5, d);
      const sunUp = smoothstep(0.0, 0.15, L.y);
      const lit = col.mul(trans).mul(float(1).add(caustic.mul(1.8 * cw).mul(trans.g).mul(sunUp).mul(uIllum.min(1))));
      // THE MIX LAW: TSL's method form `a.mix(b, t)` is mix(b, t, a) — the RECEIVER is the weight. Call mix() itself.
      col.assign(mix(col, lit, inWater));
    };
    const depth = max(uSeaLevel.sub(positionWorld.y), 0).mul(uOn);
    If(depth.greaterThan(0.02), () => {
      const L = uSunDir;
      const ly = max(L.y, 0.2);
      const trans = exp(sigma.negate().mul(depth.div(ly)));
      // Caustics along the sun's slant through the column (two Worley octaves, the water's own recipe).
      const px = positionWorld.x.add(L.x.div(ly).mul(depth));
      const pz = positionWorld.z.add(L.z.div(ly).mul(depth));
      const cUv = vec3(px.mul(0.55), pz.mul(0.55), uTime.mul(0.35));
      // PERF (shader sweep C, 2026-10-06): as the bodies' — × sunUp, exactly 0 at night; the Worleys wait for the sun.
      const caustic = float(0).toVar();
      If(L.y.greaterThan(0), () => {
        const w1 = mx_worley_noise_float(cUv);
        const w2 = mx_worley_noise_float(cUv.mul(1.9).add(vec3(3.1, 5.7, 0)));
        caustic.assign(pow(float(1).sub(w1.mul(w2)), 6));
      });
      const inWater = smoothstep(0.02, 0.5, depth);
      const sunUp = smoothstep(0.0, 0.15, L.y);
      const lit = col.mul(trans).mul(float(1).add(caustic.mul(1.8).mul(trans.g).mul(sunUp).mul(uIllum.min(1))));
      // KNOWN DEFECT, kept for the sea's exact look (shader gaps R1 brief): `col.mix(lit, inWater)` is TSL's
      // mix(lit, inWater, col) — the albedo weights the blend, so the seabed reads as half its lit colour plus half
      // white (pale and bright at any depth). The intended mix(col, lit, inWater) is the DEFAULT since 2026-09-29 (the
      // coordinator's call on agent B's finding); `atlas:sea-bed-fix = 'off'` keeps the old line bit-identical.
      If(uSeaBedFix.greaterThan(0.5), () => { col.assign(mix(col, lit, inWater)); }).Else(() => { col.assign(col.mix(lit, inWater)); });
    }).ElseIf(uBodiesOn.greaterThan(0.5), () => {
      // ── the body table: the mask cell under the fragment (render-space xz − the pack origin) ──
      const hdr = uTable.element(int(0));
      const cell = hdr.x, gn = hdr.y;
      const lx = positionWorld.x.sub(uBodyOrigin.x), lz = positionWorld.z.sub(uBodyOrigin.y);
      const colI = floor(lx.div(cell)), rowI = floor(lz.div(cell));
      If(colI.greaterThanEqual(0).and(colI.lessThan(gn)).and(rowI.greaterThanEqual(0)).and(rowI.lessThan(gn)), () => {
        const ci = rowI.mul(gn).add(colI);
        // Every index step is a product by a power-of-two reciprocal (exact on any GPU; a division may be off by ULPs).
        const q = floor(ci.mul(0.25));
        const vecIdx = floor(ci.mul(1 / 16));
        const comp = q.sub(floor(q.mul(0.25)).mul(4));
        const slot = ci.sub(q.mul(4));
        const v = uTable.element(int(vecIdx.add(MASK_BASE)));
        const hot = (x: N, k: number): N => float(1).sub(step(0.5, abs(x.sub(k))));
        const f = v.x.mul(hot(comp, 0)).add(v.y.mul(hot(comp, 1))).add(v.z.mul(hot(comp, 2))).add(v.w.mul(hot(comp, 3)));
        const inv = hot(slot, 0).add(hot(slot, 1).mul(1 / 64)).add(hot(slot, 2).mul(1 / 4096)).add(hot(slot, 3).mul(1 / 262144));
        const id = floor(f.mul(inv)).sub(floor(f.mul(inv).mul(1 / 64)).mul(64)); // 6-bit ids (water_body MASK_BITS)
        If(id.greaterThan(0.5).and(id.lessThan(LAKE_SLOTS + 0.5)), () => {
          // A lake: its slot's level and turbidity (σ = pure water + k × the constituents' share).
          const s = uTable.element(int(id.add(SLOT_BASE)));
          const d = s.x.sub(positionWorld.y);
          If(d.greaterThan(0.02), () => {
            shadeBody(d, vec3(uWaterSigma).add(vec3(uLakeSigma).sub(vec3(uWaterSigma)).mul(s.y)), lakeCaustic);
          });
          If(uBodiesDebug.greaterThan(0.5), () => { col.assign(vec3(d.mul(1 / 8), fract(id.mul(0.618034)), s.y.mul(0.5))); });
        }).ElseIf(id.greaterThan(LAKE_SLOTS + 0.5), () => {
          // A river cell: the nearest packed channel whose half-width covers the fragment sets the water level.
          const best = float(1e9).toVar();
          const water = float(-1e9).toVar();
          Loop({ start: int(0), end: int(hdr.z), type: 'int', condition: '<' }, ({ i }: { i: N }) => {
            const a = uTable.element(i.mul(2).add(RIVER_BASE));
            const b = uTable.element(i.mul(2).add(RIVER_BASE + 1));
            const dx = a.z.sub(a.x), dz = a.w.sub(a.y);
            const len2 = max(dx.mul(dx).add(dz.mul(dz)), 1e-6);
            const t = lx.sub(a.x).mul(dx).add(lz.sub(a.y).mul(dz)).div(len2).clamp(0, 1);
            const ex = lx.sub(a.x.add(dx.mul(t))), ez = lz.sub(a.y.add(dz.mul(t)));
            const dist = ex.mul(ex).add(ez.mul(ez)).sqrt();
            const hw = b.z.add(b.w.sub(b.z).mul(t));
            If(dist.lessThan(hw).and(dist.lessThan(best)), () => {
              best.assign(dist);
              water.assign(b.x.add(b.y.sub(b.x).mul(t)));
            });
          });
          const d = water.sub(positionWorld.y);
          If(d.greaterThan(0.02), () => { shadeBody(d, vec3(uRiverSigma), riverCaustic); });
          If(uBodiesDebug.greaterThan(0.5), () => { col.assign(vec3(d.mul(1 / 8), fract(id.mul(0.618034)), best.mul(0.1))); });
        });
      });
    });
    return vec4(col, base.a);
  })();
  material.userData.underwaterBed = true;
  (material as { needsUpdate?: boolean }).needsUpdate = true;
  stats.materials++;
  return true;
}
